import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import "@/App.css";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider } from "@/context/AuthContext";
import { installApiBaseShim } from "@/lib/apiBase";
import Layout from "@/components/Layout";
import HomePage from "@/pages/HomePage";
import Rankings from "@/pages/Rankings";
import Players from "@/pages/Players";
import PlayerProfile from "@/pages/PlayerProfile";
import Tournaments from "@/pages/Tournaments";
import TournamentDetail from "@/pages/TournamentDetail";
import Admin from "@/pages/Admin";
import HeadToHead from "@/pages/HeadToHead";
import LiveMatch from "@/pages/LiveMatch";
import OBSOverlay from "@/pages/OBSOverlay";
import MatchDayLive from "@/pages/MatchDayLive";
import LiveScoring from "@/pages/LiveScoring";
import AuthCallback from "@/pages/AuthCallback";
import Help from "@/pages/Help";

// Install the custom-domain API-base shim before any component mounts, so the
// very first axios call (often AuthContext fetching /auth/me) goes through it.
installApiBaseShim();

/**
 * App-wide routing.
 *
 * Canonical routes (post-2026-04-29 refactor — Phase 1+2):
 *   /                          → HomePage
 *   /matches                   → MatchDayLive (all matches: today + upcoming + results)
 *   /matches/live              → MatchDayLive (live centre)
 *   /matches/:matchId          → LiveMatch (single match view)
 *   /rankings                  → Rankings
 *   /players                   → Players
 *   /players/head-to-head      → HeadToHead
 *   /players/:playerId         → PlayerProfile
 *   /tournaments               → Tournaments
 *   /tournaments/:id           → TournamentDetail
 *   /admin                     → Admin
 *   /help · /docs              → Help
 *   /auth/callback             → AuthCallback
 *
 * Internal tools (NOT in main nav):
 *   /tools/live-scoring/:matchId → LiveScoring (referee console)
 *   /tools/obs/:matchId          → OBSOverlay (no chrome, transparent bg)
 *
 * Legacy redirects (kept so old bookmarks + share links continue to work):
 *   /home, /dashboard           → /
 *   /live, /match-day           → /matches/live
 *   /live/:matchId              → /matches/:matchId
 *   /head-to-head               → /players/head-to-head
 *   /obs/:matchId               → /tools/obs/:matchId
 */
function AppRouter() {
  const location = useLocation();

  // Auth callback uses URL fragment (#session_id=...) — handle synchronously
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }

  // OBS overlay routes render WITHOUT the main app layout (no header/footer)
  if (location.pathname.startsWith("/tools/obs/") || location.pathname.startsWith("/obs/")) {
    return (
      <Routes>
        <Route path="/tools/obs/:matchId" element={<OBSOverlay />} />
        <Route path="/obs/:matchId" element={<OBSOverlay />} />
      </Routes>
    );
  }

  return (
    <Routes>
      <Route path="/" element={<Layout />}>
        <Route index element={<HomePage />} />

        {/* --- Matches (canonical) --- */}
        <Route path="matches" element={<MatchDayLive />} />
        <Route path="matches/live" element={<MatchDayLive />} />
        <Route path="matches/:matchId" element={<LiveMatch />} />

        {/* --- Players (canonical) --- */}
        <Route path="players" element={<Players />} />
        <Route path="players/head-to-head" element={<HeadToHead />} />
        <Route path="players/:playerId" element={<PlayerProfile />} />

        {/* --- Tournaments --- */}
        <Route path="tournaments" element={<Tournaments />} />
        <Route path="tournaments/:tournamentId" element={<TournamentDetail />} />
        <Route path="tournaments/:tournamentId/:tab" element={<TournamentDetail />} />

        {/* --- Static / utility --- */}
        <Route path="rankings" element={<Rankings />} />
        <Route path="admin" element={<Admin />} />
        <Route path="help" element={<Help />} />
        <Route path="docs" element={<Help />} />
        <Route path="auth/callback" element={<AuthCallback />} />

        {/* --- Hidden tools --- */}
        <Route path="tools/live-scoring/:matchId" element={<LiveScoring />} />

        {/* --- Legacy redirects (preserve bookmarks + share-card links) --- */}
        <Route path="home" element={<Navigate to="/" replace />} />
        <Route path="dashboard" element={<Navigate to="/" replace />} />
        <Route path="live" element={<Navigate to="/matches/live" replace />} />
        <Route path="match-day" element={<Navigate to="/matches/live" replace />} />
        <Route path="live/:matchId" element={<LegacyMatchRedirect />} />
        <Route path="head-to-head" element={<Navigate to="/players/head-to-head" replace />} />
        <Route path="live-scoring/:matchId" element={<LegacyLiveScoringRedirect />} />
      </Route>
    </Routes>
  );
}

// React Router v6 doesn't preserve params on a static Navigate, so use a
// tiny component that reads the param and forwards.
function LegacyMatchRedirect() {
  const { pathname } = useLocation();
  const id = pathname.split("/").pop();
  return <Navigate to={`/matches/${id}`} replace />;
}
function LegacyLiveScoringRedirect() {
  const { pathname } = useLocation();
  const id = pathname.split("/").pop();
  return <Navigate to={`/tools/live-scoring/${id}`} replace />;
}

function App() {
  return (
    <div className="App">
      <AuthProvider>
        <BrowserRouter>
          <AppRouter />
        </BrowserRouter>
        <Toaster position="top-right" richColors />
      </AuthProvider>
    </div>
  );
}

export default App;
